import { Cancel, Edit, Error, Restore } from "@mui/icons-material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, Button, FormControl, FormControlLabel, FormLabel, Grid, OutlinedInput, Switch, Tab, Link, Tooltip, Typography, Checkbox } from "@mui/material";
import _ from "lodash";
import { FC, useCallback, useMemo, useState } from "react";
import { Address, DispatchReviewDto, EquipmentTypeDto, FreightBillingLineItemDto, LineItemBillingRatesDto, OrderDto, OrderReviewLineItemDto, PricingMethodType, RegionDto, SiteDto } from "../../../dtos";
import { OrderByType } from "../../../dtos/generated/OrderByType";
import { useCreateBillingRatesLineItemBillingRatesMutation, useGetBrokerQuery, useGetDriverQuery, useGetEquipmentTypeQuery, useGetRegionQuery, usePatchOrderReviewLineItemCancelMutation, usePatchOrderReviewLineItemRestoreByIdMutation } from "../../../store/generated/generatedApi";
import { emptyGuid, formatCurrency, LineItemTypes, QuoteType } from "../../../util";
import { EquipmentTypeSelect, OrderBySelect, SiteSelect, DatePicker, TimePicker, UnitOfMeasureSelect, MemoForm, HaulToFromForm } from "../../CommonInputs";
import { PricingMethodTypeSelect } from "../../CommonInputs/PricingMethodTypeSelect";
import { SelectableList } from "../../CommonInputs/SelectableList";
import { AuthenticatedComponent, FormInput, FormNumberInput, LoadingIndicator, useFailedActionSnackbar } from "../../CoreLib/library";
import { ConfirmationDialogWithInput } from "../../Core/ConfirmDialog/ConfirmationDialogWithInput";
import { DispatchReviewTable } from "./DispatchReviewTable";
import { isErrorInMap } from "./useOrderReviewForm";
import { getUniqueKey } from "../../../util/getUniqueKey";
import { BillingRateFormDialog } from "../BillingRateForm/BillingRatesFormDialog";
import { BilledToType, BilledToTypeToText } from "../../../dtos/enums/BilledToType";
import { adjustSecondDateToMatchFirst } from "../../../util/adjustDates";

export interface IOrderReviewLineItemTableProps {
    disabled: boolean;
    /** Defaults to `disabled`. Set separately when the memos stay editable on an otherwise locked review (RK-8309). */
    memosDisabled?: boolean;
    orderLineItems: OrderReviewLineItemDto[];
    order: OrderDto;
    addDispatchReview: (orderLineItemIndex: number) => void;
    removeDispatchReview: (orderLineItemIndex: number, dispatchIndex: number) => void;
    setOrderLineItems: (orderLineItems: React.SetStateAction<OrderReviewLineItemDto[]>) => void;
    removeOrderReviewLineItem: (orderLineItemIndex: number) => void;
    addFreightBillingLineItem: (orderLineItemIndex: number, dispatchIndex: number) => void;
    removeFreightBillingLineItem: (orderLineItemIndex: number, dispatchIndex: number, freightLineItemIndex: number) => void;
    addOrderReviewLineItem: () => void;
    handleUpdatedFreightBillingNumber: (updatedFreightBillingNumber: string, orderLineItemIndex: number, dispatchIndex: number, freightBillingLineItemIndex: number) => void;
    lineItemErrors: Map<number, Map<keyof OrderReviewLineItemDto, string>>;
    dispatchErrors: Map<number, Map<number, Map<keyof DispatchReviewDto, string>>>;
    freightErrors: Map<number, Map<number, Map<number, Map<keyof FreightBillingLineItemDto, string>>>>;
    orderPoNumber: string;
    orderJobNumber: string;
    isOrderReviewTaxable: boolean;
};

export const OrderReviewLineItemTable: FC<IOrderReviewLineItemTableProps> = (props) => {
    const {
        disabled,
        memosDisabled = disabled,
        orderLineItems,
        order,
        addDispatchReview,
        removeDispatchReview,
        setOrderLineItems,
        removeOrderReviewLineItem,
        addFreightBillingLineItem,
        removeFreightBillingLineItem,
        addOrderReviewLineItem,
        handleUpdatedFreightBillingNumber,
        lineItemErrors,
        dispatchErrors,
        freightErrors,
        orderPoNumber,
        orderJobNumber,
        isOrderReviewTaxable,
    } = props;
    const { data: equipmentTypes, isLoading: equipmentTypesLoading } = useGetEquipmentTypeQuery({ searchText: '', sorting: ['TYPE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: brokers } = useGetBrokerQuery({ searchText: '', sorting: ['CODE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: drivers } = useGetDriverQuery({ searchText: '', sorting: ['CODE+'], page: 0, pageSize: 100000, includeInactive: false });
    const { data: regions } = useGetRegionQuery({ searchText: '', sorting: ['NAME+'], page: 0, pageSize: 100000, includeInactive: false });

    const [selectedTab, setSelectedTab] = useState('0');
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(orderLineItems.length > 0 ? 0 : undefined);
    const [isBillingRatesFormDialogOpen, setIsBillingRatesFormDialogOpen] = useState<number | undefined>(undefined);
    const [getBillingRates] = useCreateBillingRatesLineItemBillingRatesMutation();
    const [isCancelLineItemMenuOpen, setIsCancelLineItemMenuOpen] = useState(false);
    const [lineItemIndexToCancel, setLineItemIndexToCancel] = useState<number | null>(null);
    const [cancelLineItem, { isLoading: isCancelingLineItem, isError: isCancelLineItemError, error: cancelLineItemError, reset: resetCancelLineItem }] = usePatchOrderReviewLineItemCancelMutation();
    const [restoreLineItem, { isLoading: isRestoringLineItem, isError: isRestoreLineItemError, error: restoreLineItemError, reset: resetRestoreLineItem }] = usePatchOrderReviewLineItemRestoreByIdMutation();

    useFailedActionSnackbar('cancel', 'the line item', isCancelLineItemError, resetCancelLineItem, cancelLineItemError);
    useFailedActionSnackbar('restore', 'the line item', isRestoreLineItemError, resetRestoreLineItem, restoreLineItemError);

    // A line item can only be canceled once it exists server-side. Line items on a review that has never been
    // saved are seeded with emptyGuid, so cancel/restore would hit the API with a placeholder id and 404.
    const isLineItemSaved = useCallback((lineItem?: OrderReviewLineItemDto) => !!lineItem?.id && lineItem.id !== emptyGuid, []);

    const handleCancelLineItem = useCallback(
        (cancellationReason: string) => {
            setIsCancelLineItemMenuOpen(false);
            if (lineItemIndexToCancel === null) return;
            const lineItem = orderLineItems[lineItemIndexToCancel];
            setLineItemIndexToCancel(null);
            if (!isLineItemSaved(lineItem)) return;
            cancelLineItem({ id: lineItem.id!, reason: cancellationReason }).then((response: any) => {
                if (response?.data) {
                    const updated = _.cloneDeep(orderLineItems);
                    updated[lineItemIndexToCancel] = { ...updated[lineItemIndexToCancel], ...response.data };
                    setOrderLineItems(updated);
                }
            });
        },
        [cancelLineItem, lineItemIndexToCancel, orderLineItems, setOrderLineItems, isLineItemSaved]
    );

    const handleRestoreLineItem = useCallback(
        (index: number) => {
            const lineItem = orderLineItems[index];
            if (!isLineItemSaved(lineItem)) return;
            restoreLineItem({ id: lineItem.id! }).then((response: any) => {
                if (response?.data) {
                    const updated = _.cloneDeep(orderLineItems);
                    updated[index] = { ...updated[index], ...response.data };
                    setOrderLineItems(updated);
                }
            });
        },
        [restoreLineItem, orderLineItems, setOrderLineItems, isLineItemSaved]
    );

    const calculateBillingRates = useCallback((billingRates: LineItemBillingRatesDto): Promise<LineItemBillingRatesDto> => {
        return getBillingRates(billingRates).then((response) => {
            return (response as any).data as LineItemBillingRatesDto;
        });
    }, [getBillingRates]);

    // See README.md "Order Line Item Quantity Fields" section for documentation on quantity field synchronization
    const handleBillingRatesChanged = useCallback(async (updatedLineItem: OrderReviewLineItemDto) => {
        if (!updatedLineItem.billingRates) {
            console.error('Unable to handle billing rates changed: billing rates are undefined');
            return updatedLineItem;
        }
        updatedLineItem.billingRates.hasBeenModified = true;
        updatedLineItem.billingRates = await calculateBillingRates(updatedLineItem.billingRates);

        return updatedLineItem;
    }, [calculateBillingRates]);

    const updateRow = useCallback(async (index: number, fieldName: string, value: any) => {
        var updatedLineItems = _.cloneDeep(orderLineItems);
        var updatedLineItem: OrderReviewLineItemDto = { ...updatedLineItems[index] };

        switch (fieldName) {
            case 'billingRates':
                updatedLineItem.billingRates = value;
                updatedLineItem = await handleBillingRatesChanged(updatedLineItem);
                break;
            case 'billingRates.quantity':
                updatedLineItem.billingRates!.quantity = value;
                updatedLineItem = await handleBillingRatesChanged(updatedLineItem);
                break;
            case 'billingRates.pricingMethodType':
                updatedLineItem.billingRates!.pricingMethodType = value;
                if (value === PricingMethodType.MaterialOnly) {
                    updatedLineItem.billingRates!.isMaterialOnly = true;
                } else {
                    updatedLineItem.billingRates!.isMaterialOnly = false;
                }
                updatedLineItem = await handleBillingRatesChanged(updatedLineItem);
                break;
            case 'loadOrQuantityReq':
                // Sync Pricing Quantity with Order By Quantity when ordering by Quantity
                // See README.md "Order Line Item Quantity Fields" section for documentation
                if (updatedLineItem.orderBy === OrderByType.Quantity) {
                    updatedLineItem.billingRates!.quantity = value;
                    updatedLineItem = await handleBillingRatesChanged(updatedLineItem);
                }
                updatedLineItem.loadOrQuantityReq = value;
                break;
            case 'unitOfMeasure':
                if (updatedLineItem.billingRates) {
                    updatedLineItem.billingRates.unitOfMeasure = value;
                }
                break;
            default:
                updatedLineItem[fieldName] = value;
                break;
        }

        if (fieldName === 'orderBy') {
            switch (value) {
                case OrderByType.Quantity:
                case OrderByType.Loads:
                    updatedLineItem.equipmentRequired = undefined;
                    break;
                case OrderByType.Equipment:
                    updatedLineItem.loadOrQuantityReq = undefined;
                    break;
                default:
                    break;
            }
        }

        if (fieldName === "isCustomPoNumber") {
            if (!value) {
                updatedLineItem["poNumber"] = orderPoNumber ?? '';
            }
        }

        if (fieldName === "isCustomerJobNumberCustom") {
            if (!value) {
                updatedLineItem["customerJobNumber"] = orderJobNumber ?? '';
            }
        }

        // Same invariant as the order form: for Quantity, loadOrQuantityReq mirrors billingRates.quantity
        // (README.md "Order Line Item Quantity Fields"). Enforcing it once here, after every field update in
        // this call, replaces separately duplicated restores for the billing-rates-changed case and the
        // switch-into-Quantity case - leaving it out either way blanks the Dispatch Requirement cell (RK-7255).
        if (updatedLineItem.orderBy === OrderByType.Quantity) {
            updatedLineItem.loadOrQuantityReq = updatedLineItem.billingRates?.quantity;
        }

        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems, handleBillingRatesChanged, orderPoNumber, orderJobNumber]);

    const handleEquipmentTypeChange = useCallback((orderLineItemIndex: number, equipmentType?: EquipmentTypeDto) => {
        setOrderLineItems((prev) => {
            return prev.map((item, i) => {
                if (i === orderLineItemIndex) {
                    return { ...item, equipmentType: equipmentType, equipmentTypeId: equipmentType?.id };
                }
                return item;
            });
        });
    }, [setOrderLineItems]);

    const handleSiteChange = useCallback((orderLineItemIndex: number) => (site?: SiteDto) => {
        setOrderLineItems((prev) => {
            return prev.map((item, i) => {
                if (i === orderLineItemIndex) {
                    return { ...item, site: site, siteId: site?.id };
                }
                return item;
            });
        });
    }, [setOrderLineItems]);

    const handleDispatchesUpdated = useCallback((orderLineItemIndex: number, dispatches: React.SetStateAction<DispatchReviewDto[]>) => {
        setOrderLineItems((prev) => {
            return prev.map((item, i) => {
                if (i === orderLineItemIndex) {
                    if (typeof dispatches === 'function') {
                        return { ...item, dispatches: dispatches(item.dispatches) };
                    }
                    else {
                        return { ...item, dispatches: dispatches };
                    }
                }
                return item;
            });
        });
    }, [setOrderLineItems]);

    const checkForLineItemGeneralErrors = useCallback((lineItemIndex: number) => isErrorInMap(lineItemErrors.get(lineItemIndex)), [lineItemErrors]);

    const checkForDispatchErrors = useCallback((lineItemIndex: number) => {
        const dispatchErrorsMap = dispatchErrors.get(lineItemIndex);
        const freightErrorsMap = freightErrors.get(lineItemIndex);

        const isDispatchErrors = Array.from(dispatchErrorsMap?.values() ?? []).some(dispatchErrors => isErrorInMap(dispatchErrors));
        const isFreightErrors = Array.from(freightErrorsMap?.values() ?? []).some(freightErrors => Array.from(freightErrors.values()).some(freightError => isErrorInMap(freightError)));

        return isDispatchErrors || isFreightErrors;
    }, [dispatchErrors, freightErrors]);

    const checkLineItemForErrors = useCallback((lineItemIndex: number) => {
        const isGeneralErrors = checkForLineItemGeneralErrors(lineItemIndex);
        const isDispatchErrors = checkForDispatchErrors(lineItemIndex);

        return isGeneralErrors || isDispatchErrors;
    }, [checkForLineItemGeneralErrors, checkForDispatchErrors]);

    const isFob = useMemo(() => {
        return order?.quote?.type === QuoteType.FOB;
    }, [order?.quote?.type]);

    const isMaterialLineItem = useCallback((orderLineItem: OrderReviewLineItemDto) => {
        return orderLineItem.lineItem?.type === LineItemTypes.Material && !isFob;
    }, [isFob]);

    const isHourlyLineItem = useCallback((orderLineItem: OrderReviewLineItemDto) => {
        return orderLineItem.lineItem?.type === LineItemTypes.Hourly;
    }, []);

    const getLineItemMaterialBillTo = useCallback((orderLineItem: OrderReviewLineItemDto) => {
        var isBilledTo = orderLineItem.billingRates?.isBilledTo ?? BilledToType.TriAx;
        var customerName = orderLineItem.customerName ?? '';
        var billedTo = orderLineItem.billingRates?.billedTo ?? '';

        return BilledToTypeToText(isBilledTo, customerName, billedTo);
    }, []);

    const handleAddressSwap = useCallback((index: number, haulFromAddress: Address, haulToAddress: Address) => {
        var updatedLineItem = orderLineItems[index];

        updatedLineItem["haulToAddressLine1"] = haulFromAddress?.addressLine1;
        updatedLineItem["haulToAddressLine2"] = haulFromAddress.addressLine2;
        updatedLineItem["haulToCity"] = haulFromAddress.city;
        updatedLineItem["haulToState"] = haulFromAddress.state;
        updatedLineItem["haulToZipCode"] = haulFromAddress.zipCode;
        updatedLineItem["haulToCountry"] = haulFromAddress.country;
        updatedLineItem["haulToDirections"] = haulFromAddress.directions;
        updatedLineItem["haulFromAddressLine1"] = haulToAddress.addressLine1;
        updatedLineItem["haulFromAddressLine2"] = haulToAddress.addressLine2;
        updatedLineItem["haulFromCity"] = haulToAddress.city;
        updatedLineItem["haulFromState"] = haulToAddress.state;
        updatedLineItem["haulFromZipCode"] = haulToAddress.zipCode;
        updatedLineItem["haulFromCountry"] = haulToAddress.country;
        updatedLineItem["haulFromDirections"] = haulToAddress.directions;
        updatedLineItem["haulFromSiteName"] = haulToAddress.siteName;
        updatedLineItem["haulToSiteName"] = haulFromAddress.siteName;
        updatedLineItem["haulFromRegion"] = haulToAddress.region;
        updatedLineItem["haulToRegion"] = haulFromAddress.region;
        updatedLineItem["haulFromRegionId"] = !!haulToAddress.region ? haulToAddress.region?.id : undefined;
        updatedLineItem["haulToRegionId"] = !!haulFromAddress.region ? haulFromAddress.region?.id : undefined;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromSiteLookup = useCallback((index: number, address: Address) => {
        var updatedLineItem = { ...orderLineItems[index] };
        updatedLineItem["haulFromAddressLine1"] = address.addressLine1;
        updatedLineItem["haulFromAddressLine2"] = address.addressLine2;
        updatedLineItem["haulFromCity"] = address.city;
        updatedLineItem["haulFromState"] = address.state;
        updatedLineItem["haulFromZipCode"] = address.zipCode;
        updatedLineItem["haulFromCountry"] = address.country;
        updatedLineItem["haulFromDirections"] = address.directions;
        updatedLineItem["haulFromSiteName"] = address.siteName;
        updatedLineItem["haulFromRegion"] = address.region;
        updatedLineItem["haulFromRegionId"] = !!address.region ? address.region?.id : undefined;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulToSiteLookup = useCallback((index: number, address: Address) => {
        var updatedLineItem = { ...orderLineItems[index] };
        updatedLineItem["haulToAddressLine1"] = address.addressLine1;
        updatedLineItem["haulToAddressLine2"] = address.addressLine2;
        updatedLineItem["haulToCity"] = address.city;
        updatedLineItem["haulToState"] = address.state;
        updatedLineItem["haulToZipCode"] = address.zipCode;
        updatedLineItem["haulToCountry"] = address.country;
        updatedLineItem["haulToDirections"] = address.directions;
        updatedLineItem["haulToSiteName"] = address.siteName;
        updatedLineItem["haulToRegion"] = address.region;
        updatedLineItem["haulToRegionId"] = !!address.region ? address.region?.id : undefined;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromRegionChange = useCallback((index: number, region: RegionDto) => {
        var updatedLineItem = { ...orderLineItems[index] };

        updatedLineItem["haulFromRegion"] = region;
        updatedLineItem["haulFromRegionId"] = region?.id;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromSiteNameChange = useCallback((index: number, siteName: string) => {
        var updatedLineItem = { ...orderLineItems[index] };

        updatedLineItem["haulFromSiteName"] = siteName;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);

    }, [orderLineItems, setOrderLineItems]);

    const handleHaulToSiteNameChange = useCallback((index: number, siteName: string) => {
        var updatedLineItem = { ...orderLineItems[index] };

        updatedLineItem["haulToSiteName"] = siteName;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);


    const handleHaulToRegionChange = useCallback((index: number, region: RegionDto) => {
        var updatedLineItem = { ...orderLineItems[index] };

        updatedLineItem["haulToRegion"] = region;
        updatedLineItem["haulToRegionId"] = region?.id;

        var updatedLineItems = _.cloneDeep(orderLineItems);
        updatedLineItems.splice(index, 1, updatedLineItem);

        setOrderLineItems(updatedLineItems);
    }, [orderLineItems, setOrderLineItems]);

    const handleHaulFromAddressChange = useCallback((index: number, address: Address) => {
        setOrderLineItems((stateValues: OrderReviewLineItemDto[]) => {
            var updatedOrderLineItems = _.cloneDeep(stateValues);
            var updatedOrderLineItem = { ...updatedOrderLineItems[index] };

            updatedOrderLineItem.haulFromAddressLine1 = address.addressLine1;
            updatedOrderLineItem.haulFromAddressLine2 = address.addressLine2;
            updatedOrderLineItem.haulFromCountry = address.country;
            updatedOrderLineItem.haulFromDirections = address.directions;
            updatedOrderLineItem.haulFromCity = address.city;
            updatedOrderLineItem.haulFromState = address.state;
            updatedOrderLineItem.haulFromZipCode = address.zipCode;
            updatedOrderLineItems[index] = updatedOrderLineItem;
            return updatedOrderLineItems;
        });
    }, [setOrderLineItems]);

    const handleHaulToAddressChange = useCallback((index: number, address: Address) => {
        setOrderLineItems((stateValues: OrderReviewLineItemDto[]) => {
            var updatedOrderLineItems = _.cloneDeep(stateValues);
            var updatedOrderLineItem = { ...updatedOrderLineItems[index] };

            updatedOrderLineItem.haulToAddressLine1 = address.addressLine1;
            updatedOrderLineItem.haulToAddressLine2 = address.addressLine2;
            updatedOrderLineItem.haulToCountry = address.country;
            updatedOrderLineItem.haulToDirections = address.directions;
            updatedOrderLineItem.haulToCity = address.city;
            updatedOrderLineItem.haulToState = address.state;
            updatedOrderLineItem.haulToZipCode = address.zipCode;
            updatedOrderLineItems[index] = updatedOrderLineItem;
            return updatedOrderLineItems;
        });
    }, [setOrderLineItems]);

    const haulToFromDetails = useCallback(
        (orderReviewLineItem: OrderReviewLineItemDto, index: number, disabled: boolean) => {
            return (
                <HaulToFromForm
                    disabled={disabled}
                    haulToAddressLine1={orderReviewLineItem?.haulToAddressLine1 ?? ''}
                    haulToAddressLine2={orderReviewLineItem?.haulToAddressLine2 ?? ''}
                    haulToCountry={orderReviewLineItem?.haulToCountry ?? ''}
                    haulToDirections={orderReviewLineItem?.haulToDirections ?? ''}
                    haulToCity={orderReviewLineItem?.haulToCity ?? ''}
                    haulToState={orderReviewLineItem?.haulToState ?? ''}
                    haulToZipCode={orderReviewLineItem?.haulToZipCode ?? ''}
                    haulFromAddressLine1={orderReviewLineItem?.haulFromAddressLine1 ?? ''}
                    haulFromAddressLine2={orderReviewLineItem?.haulFromAddressLine2 ?? ''}
                    haulFromCountry={orderReviewLineItem?.haulFromCountry ?? ''}
                    haulFromDirections={orderReviewLineItem?.haulFromDirections ?? ''}
                    haulFromCity={orderReviewLineItem?.haulFromCity ?? ''}
                    haulFromState={orderReviewLineItem?.haulFromState ?? ''}
                    haulFromZipCode={orderReviewLineItem?.haulFromZipCode ?? ''}
                    enableLookup={true}
                    regions={regions?.pageResults ?? []}
                    haulFromRegion={orderReviewLineItem?.haulFromRegion}
                    haulToRegion={orderReviewLineItem?.haulToRegion}
                    haulToSiteName={orderReviewLineItem?.haulToSiteName}
                    haulFromSiteName={orderReviewLineItem?.haulFromSiteName}
                    handleHaulFromSiteNameChange={(value) => {
                        handleHaulFromSiteNameChange(index, value);
                    }}
                    handleHaulToSiteNameChange={(value) => {
                        handleHaulToSiteNameChange(index, value);
                    }}
                    handleHaulToRegionChange={(value) => {
                        handleHaulToRegionChange(index, value);
                    }}
                    handleHaulFromRegionChange={(value) => {
                        handleHaulFromRegionChange(index, value);
                    }}
                    handleHaulFromAddressChange={(address: Address) => {
                        handleHaulFromAddressChange?.(index, address);
                    }}
                    handleHaulToAddressChange={(address: Address) => {
                        handleHaulToAddressChange?.(index, address);
                    }}
                    handleSwap={(address1: Address, address2: Address) => {
                        handleAddressSwap(index, address1, address2);
                    }}
                    handleHaulFromSiteLookup={(address: Address, siteName?: string) => {
                        handleHaulFromSiteLookup(index, address);
                    }}
                    handleHaulToSiteLookup={(address: Address, siteName?: string) => {
                        handleHaulToSiteLookup(index, address);
                    }}
                />
            );
        },
        [regions?.pageResults,
            handleHaulFromSiteNameChange,
            handleHaulToSiteNameChange,
            handleHaulToRegionChange,
            handleHaulFromRegionChange,
            handleHaulFromAddressChange,
            handleHaulToAddressChange,
            handleAddressSwap,
            handleHaulFromSiteLookup,
            handleHaulToSiteLookup,
        ]
    );

    const convertLineItemTypeToText = useCallback((lineItemType: string | undefined) => {
        if (!lineItemType) {
            return '';
        }

        switch (lineItemType) {
            case LineItemTypes.Material:
                return 'material';
            case LineItemTypes.Hourly:
                return 'hourly';
            case LineItemTypes.SiteHauling:
                return 'siteHauling';
            case LineItemTypes.Dump:
                return 'dump';
            case LineItemTypes.Misc:
                return 'misc';
            default:
                return lineItemType;
        }
    }, []);

    const generalDetails = useCallback((orderLineItem: OrderReviewLineItemDto, index: number) => {
        var errors = lineItemErrors.get(index);
        const isMaterial = isMaterialLineItem(orderLineItem);
        const isHourly = isHourlyLineItem(orderLineItem);
        const isMisc = orderLineItem.lineItem?.type === LineItemTypes.Misc;
        const hideSiteAndZone = isHourly || isMisc;
        const topRowFieldXs = hideSiteAndZone ? 3 : 2;
        const isLineItemCanceled = !!orderLineItem.canceledOn;
        const fieldDisabled = disabled || isLineItemCanceled;
        const isSaved = isLineItemSaved(orderLineItem);

        return (
            <Box p={2} key={getUniqueKey(orderLineItem)}>
                {isLineItemCanceled && (
                    <Box mb={1} p={1} bgcolor='action.disabledBackground' borderRadius={1}>
                        <Typography variant='body2' color='text.secondary'>
                            <strong>Canceled</strong>
                            {orderLineItem.cancellationReason ? ` — Reason: ${orderLineItem.cancellationReason}` : ''}
                            {orderLineItem.canceledByUser ? ` — By: ${orderLineItem.canceledByUser}` : ''}
                        </Typography>
                    </Box>
                )}
                <Box sx={{ pointerEvents: isLineItemCanceled ? 'none' : undefined, opacity: isLineItemCanceled ? 0.5 : 1 }}>
                <Grid item container direction='column' spacing={1}>
                    <Grid item container direction='row' xs={12} spacing={1} wrap='nowrap'>
                        <Grid item xs={topRowFieldXs}>
                            <FormNumberInput
                                value={orderLineItem.orderLineItemNumber}
                                name='orderLineItemNumber'
                                label='Line Item #'
                                fullWidth
                                disabled={fieldDisabled}
                                onChange={(event) => {
                                    updateRow(index, 'orderLineItemNumber', isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
                                }}
                                error={!!errors?.get('orderLineItemNumber')}
                                errorText={errors?.get('orderLineItemNumber')}
                            />
                        </Grid>
                        <Grid item xs={topRowFieldXs}>
                            <FormInput
                                value={orderLineItem?.description}
                                onChange={(event) => {
                                    updateRow(index, 'description', event.target.value);
                                }}
                                label='Description'
                                fullWidth
                                errorText={errors?.get('description')}
                                disabled={fieldDisabled}
                            />
                        </Grid>
                        <Grid item xs={topRowFieldXs}>
                            <EquipmentTypeSelect
                                selectedEquipmentTypeId={orderLineItem?.equipmentType?.id ?? ''}
                                handleSelectedEquipmentTypeChange={(value) => {
                                    handleEquipmentTypeChange(index, value ?? undefined);
                                }}
                                disabled={fieldDisabled || equipmentTypes === undefined}
                                errorMessage={errors?.get('equipmentType')}
                            />
                        </Grid>
                        {!hideSiteAndZone && (
                            <>
                                <Grid item xs={2}>
                                    <SiteSelect
                                        selectedSiteId={orderLineItem.siteId ?? ''}
                                        handleSelectedSiteChange={handleSiteChange(index)}
                                        hideCopyAddressButton
                                        disabled={fieldDisabled}
                                    />
                                </Grid>
                                <Grid item xs={2}>
                                    <FormInput
                                        value={orderLineItem.zone}
                                        onChange={(event) => {
                                            updateRow(index, 'zone', event.target.value);
                                        }}
                                        label='Zone'
                                        fullWidth
                                        disabled={fieldDisabled}
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={topRowFieldXs} container alignItems="center" justifyContent="center">
                            <Link
                                variant='body2'
                                href={`/quote/${order.quote?.id}/lineItem/${convertLineItemTypeToText(orderLineItem.lineItem?.type)}/${orderLineItem.lineItem?.id}/`}
                                target="_blank"
                                rel="noopener noreferrer">
                                {orderLineItem.lineItem?.type} Line Item for Quote #{order.quote?.quoteNumber}
                            </Link>
                        </Grid>
                    </Grid>
                    <Grid item container direction='row' xs={12} spacing={1} wrap='nowrap'>
                        {isMaterial && <Grid item xs={3}>
                            <PricingMethodTypeSelect
                                selectedPricingMethodType={orderLineItem.billingRates!.pricingMethodType!}
                                onChange={(value) => {
                                    updateRow(index, 'billingRates.pricingMethodType', value);
                                }}
                                disabled={fieldDisabled}
                            />
                        </Grid>}
                        <Grid item xs={isMaterial ? 2 : 4}>
                            <>
                                <FormControl fullWidth disabled>
                                    <FormLabel>{'Rate ' + (orderLineItem.billingRates?.flatRateApplied && isMaterial ? '(+ Flat)' : '')}</FormLabel>
                                    <OutlinedInput
                                        value={formatCurrency(orderLineItem.billingRates?.salesPrice ?? orderLineItem.billingRates?.quoteRate ?? orderLineItem.billingRates?.lineItemRate)}
                                        disabled
                                        inputProps={{ min: 0 }}
                                        endAdornment={<Edit color='secondary' sx={{ cursor: 'pointer' }} onClick={() => {
                                            setIsBillingRatesFormDialogOpen(index);
                                        }} />}
                                    />
                                </FormControl>
                                <BillingRateFormDialog
                                    open={isBillingRatesFormDialogOpen === index}
                                    onClose={() => setIsBillingRatesFormDialogOpen(undefined)}
                                    onConfirm={(formValues) => {
                                        updateRow(index, 'billingRates', formValues);
                                    }}
                                    billingRates={orderLineItem.billingRates!}
                                    lineItem={orderLineItem}
                                    isFob={isFob}
                                    isOrder
                                    disabled={fieldDisabled}
                                    customerName={order?.quote?.customer?.name}
                                />
                            </>
                        </Grid>
                        <Grid item xs={isMaterial ? 2 : 4}>
                            {/* Pricing Quantity field - See README.md "Order Line Item Quantity Fields" section for documentation */}
                            <FormNumberInput
                                label='Quantity'
                                name='quantity'
                                value={orderLineItem.billingRates?.quantity}
                                onChange={(e) => updateRow(index, 'billingRates.quantity', isNaN(e.target.valueAsNumber) ? undefined : e.target.valueAsNumber)}
                                fullWidth
                                inputProps={{ min: 0 }}
                                disabled={fieldDisabled}
                            />
                        </Grid>
                        <Grid item xs={(isMaterial || isHourly) ? 3 : 4}>
                            {!!orderLineItem.lineItemId ? <FormInput
                                value={orderLineItem?.billingRates?.unitOfMeasure}
                                onChange={() => { }}
                                label='Units'
                                fullWidth
                                disabled
                            /> :
                                <UnitOfMeasureSelect
                                    selectedUnit={orderLineItem?.billingRates?.unitOfMeasure ?? ''}
                                    handleSelectedUnitChange={(value) => updateRow(index, 'unitOfMeasure', value)}
                                    disabled={fieldDisabled}
                                    errorMessage={errors?.get('unitOfMeasure')}
                                />}
                        </Grid>
                        {(isMaterial || isHourly) && (
                            <Grid item xs={2}>
                                <FormInput
                                    value={getLineItemMaterialBillTo(orderLineItem)}
                                    onChange={() => { }}
                                    label='Material Bill To'
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Grid item container spacing={1} xs={12}>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.yardTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(order.orderDate, date);
                                        updateRow(index, 'yardTime', adjustedDate);
                                    }
                                }}
                                label={`Yard Time`}
                                disabled={fieldDisabled}
                                error={!!errors?.get('yardTime')}
                                errorText={errors?.get('yardTime')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TimePicker
                                value={orderLineItem.onsiteTime}
                                onChange={(date) => {
                                    if (date) {
                                        var adjustedDate = adjustSecondDateToMatchFirst(order.orderDate, date);
                                        updateRow(index, 'onsiteTime', adjustedDate);
                                    }
                                }}
                                label={`Onsite Time`}
                                disabled={fieldDisabled}
                                error={!!errors?.get('onsiteTime')}
                                errorText={errors?.get('onsiteTime')}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <DatePicker
                                value={order.quote?.expirationDate}
                                onChange={() => { }}
                                label={`Quote Expiration Date`}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <OrderBySelect
                                selectedOrderBy={orderLineItem.orderBy!}
                                onChange={(value) => {
                                    updateRow(index, 'orderBy', value);
                                }}
                                disabled={fieldDisabled}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            {orderLineItem.orderBy !== OrderByType.Equipment && (
                                <FormNumberInput
                                    value={orderLineItem.loadOrQuantityReq}
                                    name='loadOrQuantityReq'
                                    onChange={(event) => {
                                        updateRow(index, 'loadOrQuantityReq', isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber);
                                    }}
                                    label='Quantity'
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    disabled={fieldDisabled}
                                    error={!!errors?.get('loadOrQuantityReq')}
                                    errorText={errors?.get('loadOrQuantityReq')}
                                />
                            )}
                            {!orderLineItem.orderBy && (
                                <FormNumberInput
                                    value={orderLineItem.equipmentRequired}
                                    name='equipmentRequired'
                                    onChange={(event) => {
                                        updateRow(
                                            index,
                                            'equipmentRequired',
                                            isNaN(event.target.valueAsNumber) ? undefined : event.target.valueAsNumber
                                        );
                                    }}
                                    label='Equipment Req.'
                                    fullWidth
                                    inputProps={{ min: 0 }}
                                    disabled={fieldDisabled}
                                    error={!!errors?.get('equipmentRequired')}
                                    errorText={errors?.get('equipmentRequired')}
                                />
                            )}
                        </Grid>
                        <Grid item xs={2} container alignItems="center" justifyContent="center">
                            <FormControlLabel
                                control={<Switch checked={orderLineItem.isCustomPoNumber} disabled={fieldDisabled} onChange={(event) => { updateRow(index, 'isCustomPoNumber', event.target.checked) }} />}
                                label='Custom PO #'
                                labelPlacement='end'
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormInput
                                value={orderLineItem.poNumber}
                                label='PO Number'
                                fullWidth
                                onChange={(event) => {
                                    updateRow(index, 'poNumber', event.target.value);
                                }}
                                disabled={fieldDisabled || !orderLineItem.isCustomPoNumber}
                                errorText={errors?.get('poNumber')}
                                maxLength={30}
                            />
                        </Grid>
                        <Grid item xs={2} container alignItems="center" justifyContent="center">
                            <FormControlLabel
                                control={<Switch checked={orderLineItem.isCustomerJobNumberCustom} disabled={fieldDisabled} onChange={(event) => { updateRow(index, 'isCustomerJobNumberCustom', event.target.checked) }} />}
                                label='Custom Job #'
                                labelPlacement='end'
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <FormInput
                                value={orderLineItem.customerJobNumber}
                                label='Job Number'
                                fullWidth
                                onChange={(event) => {
                                    updateRow(index, 'customerJobNumber', event.target.value);
                                }}
                                disabled={fieldDisabled || !orderLineItem.isCustomerJobNumberCustom}
                            />
                        </Grid>
                        {isMaterialLineItem(orderLineItem) && (
                            <Grid item xs={2} container alignItems="center" justifyContent="center">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={orderLineItem.isMaterialTaxable ?? true}
                                            onChange={(_, checked) => updateRow(index, 'isMaterialTaxable', checked)}
                                            disabled={fieldDisabled || !isOrderReviewTaxable}
                                        />
                                    }
                                    label='Is Material Taxable'
                                />
                            </Grid>
                        )}
                    </Grid>
                    {haulToFromDetails(orderLineItem, index, fieldDisabled)}
                </Grid>
                </Box>
                <Box mt={1} display='flex' justifyContent='flex-end'>
                    <AuthenticatedComponent
                        requiredPermissions={['cancel:orders']}
                        logic='and'
                        children={
                            <Tooltip title={isSaved ? '' : 'Save the order review before canceling this line item.'}>
                                <span>
                                    <Button
                                        size='small'
                                        variant='contained'
                                        endIcon={isLineItemCanceled ? <Restore /> : <Cancel />}
                                        disabled={disabled || isCancelingLineItem || isRestoringLineItem || !isSaved || (!isLineItemCanceled && !orderLineItem.isCancelable)}
                                        onClick={() => {
                                            if (isLineItemCanceled) {
                                                handleRestoreLineItem(index);
                                            } else {
                                                setLineItemIndexToCancel(index);
                                                setIsCancelLineItemMenuOpen(true);
                                            }
                                        }}>
                                        {isLineItemCanceled ? 'Restore' : 'Cancel'}
                                    </Button>
                                </span>
                            </Tooltip>
                        }
                    />
                </Box>
            </Box>
        );
    }, [lineItemErrors, isMaterialLineItem, isHourlyLineItem, disabled, equipmentTypes, handleSiteChange, order.quote?.id, order.quote?.quoteNumber, order.quote?.expirationDate, order.quote?.customer?.name, order.orderDate, convertLineItemTypeToText, isBillingRatesFormDialogOpen, isFob, getLineItemMaterialBillTo, haulToFromDetails, updateRow, handleEquipmentTypeChange, isOrderReviewTaxable, handleRestoreLineItem, isLineItemSaved, isCancelingLineItem, isRestoringLineItem]);

    const dispatchDetails = useCallback((orderLineItem: OrderReviewLineItemDto, orderLineItemIndex: number) => {
        return (
            <Box flexGrow={1} overflow='hidden'>
                <DispatchReviewTable
                    brokers={brokers?.pageResults ?? []}
                    disabled={disabled || !!orderLineItem.canceledOn}
                    dispatches={orderLineItem.dispatches ?? []}
                    drivers={drivers?.pageResults ?? []}
                    orderReviewLineItem={orderLineItem}
                    order={order}
                    removeDispatchReview={(dispatchIndex) => removeDispatchReview(orderLineItemIndex, dispatchIndex)}
                    setDispatches={(dispatches) => handleDispatchesUpdated(orderLineItemIndex, dispatches)}
                    addFreightBillingLineItem={(dispatchIndex) => addFreightBillingLineItem(orderLineItemIndex, dispatchIndex)}
                    removeFreightBillingLineItem={(dispatchIndex, freightLineItemIndex) => removeFreightBillingLineItem(orderLineItemIndex, dispatchIndex, freightLineItemIndex)}
                    addDispatchReview={() => addDispatchReview(orderLineItemIndex)}
                    dispatchErrors={dispatchErrors.get(orderLineItemIndex) ?? new Map()}
                    freightErrors={freightErrors.get(orderLineItemIndex) ?? new Map()}
                    setOrderReviewLineItem={(lineItem) => setOrderLineItems(orderLineItems.map((item, index) => index === orderLineItemIndex ? lineItem : item))}
                    handleUpdatedFreightBillingNumber={handleUpdatedFreightBillingNumber}
                />
            </Box>
        );
    }, [addDispatchReview, addFreightBillingLineItem, brokers?.pageResults, disabled, drivers?.pageResults, handleDispatchesUpdated, order, removeDispatchReview, removeFreightBillingLineItem, dispatchErrors, freightErrors, setOrderLineItems, orderLineItems, handleUpdatedFreightBillingNumber]);

    const memosTab = useCallback((orderLineItem: OrderReviewLineItemDto, index?: number) => {
        const isLineItemCanceled = !!orderLineItem.canceledOn;
        return (
            <Box key={getUniqueKey(orderLineItem)} sx={{ pointerEvents: isLineItemCanceled ? 'none' : undefined, opacity: isLineItemCanceled ? 0.5 : 1 }}>
                <MemoForm
                    quoteMemo={order?.quote?.memo}
                    lineItemMemo={orderLineItem.lineItem?.lineItemMemo}
                    dispatchBillingMemo={orderLineItem.dispatchBillingMemo}
                    orderLineItemDriverMemo={orderLineItem.driverMemo}
                    hideDriverComment={true}
                    handleOrderLineItemDriverMemoChange={(value) => {
                        updateRow(index!, 'driverMemo', value);
                    }}
                    handleDispatchBillingMemoChange={(value) => {
                        updateRow(index!, 'dispatchBillingMemo', value);
                    }}
                    disabled={memosDisabled}
                />
            </Box>
        );
    }, [order?.quote?.memo, memosDisabled, updateRow]);

    const details = useCallback((orderLineItem?: OrderReviewLineItemDto, index?: number) => {
        const isGeneralErrors = index !== undefined && checkForLineItemGeneralErrors(index);
        const isDispatchErrors = index !== undefined && checkForDispatchErrors(index);

        return (
            <Box key={orderLineItem?.id} sx={{ width: '100%', borderBottom: 1, borderRight: 1, borderColor: 'divider', display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}>
                <TabContext value={orderLineItem ? selectedTab : ''}>
                    <Box sx={{ width: '100%', borderTop: 1, borderBottom: 1, borderColor: 'divider' }}>
                        <TabList variant='fullWidth' sx={{ width: '100%' }} onChange={(_e, newValue) => setSelectedTab(newValue)}>
                            <Tab label='General' value='0' disabled={!orderLineItem} iconPosition='end' icon={isGeneralErrors ? <Error color='error' /> : undefined} sx={{ minHeight: '48px' }} />
                            <Tab label='Memos' value='1' disabled={!orderLineItem} iconPosition='end' sx={{ minHeight: '48px' }} />
                            <Tab label='Dispatches' value='2' disabled={!orderLineItem} iconPosition='end' icon={isDispatchErrors ? <Error color='error' /> : undefined} sx={{ minHeight: '48px' }} />
                        </TabList>
                    </Box>
                    {!orderLineItem && <Box textAlign='center'><Typography mt={2} color='GrayText'>No Item Selected</Typography></Box>}
                    <TabPanel value='0' sx={{ padding: '5px', overflowY: 'auto' }}>
                        {orderLineItem && index !== undefined && generalDetails(orderLineItem, index)}
                    </TabPanel>
                    <TabPanel value='1' sx={{ overflowY: 'auto' }}>
                        {orderLineItem && index !== undefined && memosTab(orderLineItem, index)}
                    </TabPanel>
                    <TabPanel value='2' sx={{ padding: '0px', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
                        {orderLineItem && index !== undefined && dispatchDetails(orderLineItem, index)}
                    </TabPanel>
                </TabContext>
            </Box>
        );
    }, [checkForLineItemGeneralErrors, checkForDispatchErrors, selectedTab, generalDetails, memosTab, dispatchDetails]);

    if (equipmentTypesLoading) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', flexGrow: 1, height: '100%' }}>
                <SelectableList
                    items={orderLineItems}
                    selectedIndex={selectedIndex}
                    setSelectedIndex={setSelectedIndex}
                    hasError={checkLineItemForErrors}
                    getKey={(item, index) => item.sourceOrderLineItemId ?? (item as OrderReviewLineItemDto & { clientTempId?: string }).clientTempId ?? item.id ?? `row-${index}`}
                    getDesc={(item) => `${item.orderLineItemNumber} ${item.description}`}
                    getListItemButtonSx={(item) => (item.canceledOn ? { opacity: 0.5 } : undefined)}
                    style={{ border: 1, borderColor: 'divider', minWidth: 240, bgcolor: 'background.paper', overflowY: 'auto', height: '100%' }}
                    addItem={addOrderReviewLineItem}
                    disableAdd={disabled}
                    removeItem={(_item, index) => { removeOrderReviewLineItem(index); }}
                    disableRemove={(item: OrderReviewLineItemDto) => item.isDispatched || item.isInvoiced || disabled}
                />
                {details(selectedIndex !== undefined ? orderLineItems[selectedIndex] : undefined, selectedIndex)}
            </Box>
            <ConfirmationDialogWithInput
                open={isCancelLineItemMenuOpen}
                onClose={() => { setIsCancelLineItemMenuOpen(false); setLineItemIndexToCancel(null); }}
                formLabel={'Please enter a cancelation reason.'}
                title={'Cancel Review Line Item'}
                titleIcon={<Cancel />}
                cancelText={'Cancel'}
                confirmText={'Save'}
                save={handleCancelLineItem}
                requireConfirmation={false}
            />
        </>
    );
};